// Описание эндпоинта — одна запись реестра на строку таблиц tech/03. По ней:
// сервер валидирует запрос и (в тестах) ответ, клиент типизирует вызовы, генерируются OpenAPI и фикстуры.
import type { z } from 'zod';
import type { ErrorCode } from './errors.js';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Класс лимита частоты (03 §1.2). */
export type RateClass = 'auth' | 'read' | 'write' | 'turn' | 'shop' | 'analytics' | 'sync' | 'chat' | 'none';

/**
 * Чем гасится повтор (03 §1.1 «Как читать столбцы … Идемп.»):
 * `key-required` — Idempotency-Key обязателен; `key` — принимается; `state` — по состоянию объекта;
 * `natural` — естественный ключ (ход боя по turnNo+moveId, шаг обучения); `none` — повтор безвреден; `read` — GET.
 */
export type Idempotency = 'key-required' | 'key' | 'state' | 'natural' | 'none' | 'read';

export interface EndpointDef<
  P extends z.ZodType = z.ZodType,
  Q extends z.ZodType = z.ZodType,
  B extends z.ZodType = z.ZodType,
  R extends z.ZodType = z.ZodType,
> {
  /** Стабильное имя: `<раздел>.<действие>`. */
  id: string;
  method: HttpMethod;
  /** Путь после `/api/v1`, параметры как `{battleId}`. */
  path: string;
  /** Нужен ли Bearer JWT. */
  auth: boolean;
  /** Нужна ли подпись X-Req-* (03 §1, решение Р-04 о списке). */
  signed: boolean;
  idempotency: Idempotency;
  rate: RateClass;
  params?: P;
  query?: Q;
  body?: B;
  response: R;
  /** HTTP-статус успеха; 204 у `GET /battles/active` без боя — отдельным полем. */
  /** 202 — отложенное действие (роспуск кооператива, 03 §7). */
  status: 200 | 201 | 202;
  allowNoContent?: boolean;
  /** Свои коды сверх общих (03 §1.1). */
  errors: readonly ErrorCode[];
  /** Ссылка на раздел спецификации. */
  spec: string;
  /** Этап и задача бэклога, где эндпоинт реализуется. */
  task: string;
}

export function endpoint<P extends z.ZodType, Q extends z.ZodType, B extends z.ZodType, R extends z.ZodType>(
  def: EndpointDef<P, Q, B, R>,
): EndpointDef<P, Q, B, R> {
  return def;
}

export type BodyOf<E> = E extends EndpointDef<z.ZodType, z.ZodType, infer B, z.ZodType> ? z.input<B> : never;
export type ResponseOf<E> = E extends EndpointDef<z.ZodType, z.ZodType, z.ZodType, infer R> ? z.output<R> : never;
export type ParamsOf<E> = E extends EndpointDef<infer P, z.ZodType, z.ZodType, z.ZodType> ? z.input<P> : never;
export type QueryOf<E> = E extends EndpointDef<z.ZodType, infer Q, z.ZodType, z.ZodType> ? z.input<Q> : never;
